import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Eye, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Banner fisso in cima che indica all'admin che sta navigando come un altro
 * utente (modalità Demo "Vedi come..."). Visibile solo quando user.impersonator
 * è popolato dal backend (cookie triolo_impersonate_uid attivo).
 */
export default function ImpersonationBanner() {
  const { user, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);

  // Reset del flag `exiting` ad ogni nuovo ciclo di impersonificazione.
  // Senza questo, dopo un impersonate→stop→impersonate consecutivo nella
  // stessa sessione SPA, il pulsante "Esci" può rimanere disabilitato.
  useEffect(() => {
    setExiting(false);
  }, [user?.impersonator?.id]);

  if (!user || !user.impersonator) return null;

  const roleLabel = {
    admin: "Admin",
    agent: "Agente",
    client: "Cliente",
    magazziniere: "Magazziniere",
  }[user.role] || user.role;

  const handleStop = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      await stopImpersonate();
      toast.success(`Modalità demo terminata. Sei di nuovo ${user.impersonator.name}.`);
      // Riporta l'admin sulla sua dashboard
      navigate("/admin/users", { replace: true });
    } catch {
      toast.error("Errore nella chiusura della modalità demo");
      setExiting(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] w-full bg-gradient-to-r from-fuchsia-600 via-purple-600 to-fuchsia-600 border-b-2 border-fuchsia-400 shadow-[0_4px_16px_rgba(217,70,239,0.35)]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3">
        <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs sm:text-sm font-semibold leading-tight truncate">
            <span className="hidden sm:inline">Stai navigando come </span>
            <span className="sm:hidden">Come </span>
            <span className="font-bold underline decoration-fuchsia-200 decoration-2 underline-offset-2">
              {user.name}
            </span>
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded bg-white/20 text-[10px] sm:text-xs font-mono shrink-0">
              {roleLabel}
            </span>
          </p>
          <p className="text-fuchsia-100 text-[10px] sm:text-xs leading-tight truncate">
            Admin: {user.impersonator.name} · {user.impersonator.email}
          </p>
        </div>
        <button
          type="button"
          onClick={handleStop}
          disabled={exiting}
          data-testid="impersonation-stop-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md bg-white text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-60 text-xs sm:text-sm font-bold transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Esci dalla demo</span>
          <span className="sm:hidden">Esci</span>
        </button>
      </div>
    </div>
  );
}
